import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Auth, cqAPI, getBid } from "../../lib/Auth";
import SendArea from "./SendArea";
import Messages from "./Messages";
import UserInfo from "./UserInfo";
import "./Inbox.css";
import { useColorMode, useTheme, useToast } from "@chakra-ui/react";
import { useAppContext } from "../../AppContext";
const _ = require("lodash");

const DRAFT_FETCH_LIMIT = 5;

function InboxMessages({ removeActionBarItem, addActionBarItem, isMobile }) {
  const { uid } = useParams();
  const bid = getBid();
  const [typing, setTyping] = useState(false);
  const [messages, setMessages] = useState([]);
  const [disableSendButton, setDisableSendButton] = useState(false);
  const [messageType, setMessageType] = useState([]);
  const toast = useToast();
  const [isStripeConnected, setIsStripeConnected] = useState(false);
  const theme = useTheme();
  const [relatedUid, setRelatedUid] = useState(uid)
  const { colorMode } = useColorMode();
  const {
    staff,
    setStaff,
    realtime,
    realtimeInitialized,
    location,
    isPatient,
    handleDraftButtonClick,
  } = useAppContext();

  const [userInfo, setUserInfo] = useState({});
  const [draft, setDraft] = useState("");
  const [isDraftBoxOpen, setIsDraftBoxOpen] = useState(false);
  const [conversationClosed, setConversationClosed] = useState(false);

  const fetchAllMessages = async (update) => {
    const response = await cqAPI.get(`/messages?from=${uid}`);
    if (response.type === "ok") {
      setMessages(response?.data?.conversations || []);
    }
    if (response.type === "error"){
      toast({
        title: 'Error',
        description: response.msg,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return
    }
    setMessages(response.data.conversations);
    if (window.location.pathname.startsWith("/task")){
      setRelatedUid(response.data.conversations.find(c=>c.related_uid)?.related_uid || uid)
    }
    // check last message for draft to reduce delay
    if (response.data?.conversations?.length && response.data.conversations[response.data.conversations.length - 1].type === 'draft'){
      setDraft(response.data.conversations[response.data.conversations.length - 1].body);
      setIsDraftBoxOpen(true);
    }

    setUserInfo(response.data.userInfo);
    const conversationOnly = response.data.conversations.filter(c=>'closed' in c);
    setConversationClosed(
      conversationOnly.length
        ? conversationOnly[conversationOnly.length - 1]
          .closed
        : false
    );

    const types = [];
    if (
      response.data.userInfo?.possible_notification_pref?.indexOf("email") !== -1
    ) {
      types.push("email");
    }
    if (
      response.data.userInfo?.possible_notification_pref?.indexOf("sms") !== -1
    ) {
      types.push("sms");
    }
    if (!update) setMessageType(types);
    if (!staff.length) {
      const s = await cqAPI.get("/list-staff");
      if (s.data && s.data.length) {
        setStaff(s.data);
      }
    }
  };

  let typingTimer;

  const showTyping = (payload, arg) => {
    if (payload.data.uid === Auth.user.id) {
      return;
    }
    if (payload.data.target_uid !== uid) {
      return;
    }
    if (typingTimer) {
      clearTimeout(typingTimer);
    }
    setTyping(true);
    typingTimer = setTimeout(() => {
      setTyping(false);
      typingTimer = null;
    }, 5000);
  };

  const fetchLastDraftMessage = async () => {
    try {
      const response = await cqAPI.get(`/messages/draft?from=${uid}`);
      if (response && response.data && response.data.body) {
        return String(response.data.body);
      }
    } catch (error) {
      console.log("error fetching draft message: ", error);
    }
    return "";
  };

  const updateDraft = async () => {
    if (
      messages[messages.length - 1]?.type !== undefined &&
      messages[messages.length - 1].type !== "call" &&
      messages[messages.length - 1]?.direction !== undefined &&
      messages[messages.length - 1].direction !== "internal_note" &&
      messages[messages.length - 1].direction !== "outgoing"
    ) {
      const tmpDraft = await fetchLastDraftMessage();
      if (!_.isEqual(tmpDraft, draft)){
        setDraft(tmpDraft);
        setIsDraftBoxOpen(true);
      }
    }
    // draft already sent
    if (messages[messages.length - 1]?.type !== "draft" && messages[messages.length - 1]?.direction === "outgoing") {
      setIsDraftBoxOpen(false);
      setDraft("");
    }
  };

  useEffect(() => {
    let counter = 0;
    const draftIntervalId = setInterval(async () => {
      if (counter >= DRAFT_FETCH_LIMIT) {
        clearInterval(draftIntervalId);
        return;
      }
      await updateDraft();
      counter += 1;
    }, 3000);
    return () => {
      clearInterval(draftIntervalId);
    };
  }, [messages]);

  const handleNewMessage = async (update) => {
    await fetchAllMessages(update);
    await updateDraft();
  };

  useEffect(() => {
    if (!uid) {
      return;
    }
    handleNewMessage();
  }, [uid]);

  useEffect(() => {
    if (!realtime.current) {
      return;
    }
    const UpdateTopics = [
      `conv_status_change/${bid}/${uid}`,
      `_new_chat_message/${bid}/${uid}`,
    ];
    UpdateTopics.forEach((topic) =>
      realtime.current.subscribeToTopic(topic, bid, handleNewMessage)
    );
    realtime.current.subscribeToTopic(
      `chat_typing/${bid}/${uid}`,
      bid,
      showTyping
    );
  }, [realtimeInitialized, uid]);

  useEffect(() => {
    return () => {
      if (!realtime.current) {
        return;
      }
      const UpdateTopics = [
        `conv_status_change/${bid}/${uid}`,
        `_new_chat_message/${bid}/${uid}`,
      ];
      UpdateTopics.forEach((topic) =>
        realtime.current.unsubscribeFromTopic(topic, bid, () =>
          handleNewMessage(true)
        )
      );
      realtime.current.unsubscribeFromTopic(
        `chat_typing/${bid}/${uid}`,
        bid,
        showTyping
      );
    };
  }, [location]);

  return (
    <div className="inbox-container">
      <div className="chat-area">
        <Messages
          messages={messages}
          disableSendButton={disableSendButton}
          userInfo={userInfo}
          colorMode={colorMode}
          isPatient={isPatient}
          fetchAllMessages={fetchAllMessages}
          setMessages={setMessages}
          removeActionBarItem={removeActionBarItem}
          addActionBarItem={addActionBarItem}
          isMobile={isMobile}
        />

        <SendArea
          isPatient={isPatient}
          disableSendButton={disableSendButton}
          userInfo={userInfo}
          toast={toast}
          setUserInfo={setUserInfo}
          messageType={messageType}
          setMessageType={setMessageType}
          typing={typing}
          colorMode={colorMode}
          uid={uid}
          isStripeConnected={isStripeConnected}
          setIsStripeConnected={setIsStripeConnected}
          setDisableSendButton={setDisableSendButton}
          staff={staff}
          draft={draft}
          setDraft={setDraft}
          handleDraftButtonClick={handleDraftButtonClick}
          isDraftBoxOpen={isDraftBoxOpen}
          setIsDraftBoxOpen={setIsDraftBoxOpen}
          isMobile={isMobile}
        />
      </div>
      {!isPatient && (
        <UserInfo
          toast={toast}
          userInfo={userInfo}
          uid={uid}
          relatedUid={relatedUid}
          isStripeConnected={isStripeConnected}
          conversationClosed={conversationClosed}
          setConversationClosed={setConversationClosed}
          messages={messages}
          setUserInfo={setUserInfo}
          addActionBarItem={addActionBarItem} // Pass the functions
          removeActionBarItem={removeActionBarItem}
          isMobile={isMobile}
        />
      )}
    </div>
  );
}

export default InboxMessages;
